/*
 * FICHIER: matomoService.js
 * BUT: Service pour gérer l'analytics web avec Matomo
 * 
 * Matomo est une alternative open-source à Google Analytics qui permet de :
 * - Tracker les pages visitées par les utilisateurs
 * - Mesurer les interactions (clics, temps passé, etc.)
 * - Suivre les conversions et objectifs
 * - Analyser les erreurs JavaScript
 * - Respecter la vie privée (données hébergées sur notre serveur)
 * 
 * Ce service encapsule toutes les interactions avec Matomo dans une classe
 * réutilisable avec gestion d'erreurs et configuration par environnement.
 */

/**
 * Service Matomo pour le tracking des statistiques
 * Gère l'initialisation et toutes les interactions avec Matomo Analytics
 * 
 * PATTERN SINGLETON : Une seule instance de ce service existe dans toute l'app
 */
class MatomoService {
  constructor() {
    // État d'initialisation - evite les doubles initialisations
    this.isInitialized = false;
    
    // Configuration basée sur l'environnement (dev vs prod)
    this.config = this.getConfig();
    
    // Initialisation automatique à la création
    this.init();
  }

  /**
   * Configuration basée sur l'environnement
   * 
   * ENVIRONNEMENTS :
   * - Développement : Matomo local sur localhost (optionnel)
   * - Production : Matomo hébergé sur stats.offisport.com
   * 
   * @returns {Object} Configuration Matomo pour l'environnement actuel
   */
  getConfig() {
    // Détection de l'environnement
    const isDevelopment = process.env.NODE_ENV === 'development';
    const isLocalhost = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1';
    
    // CONFIGURATION DÉVELOPPEMENT (local)
    if (isDevelopment || isLocalhost) {
      return {
        environment: 'development',
        url: 'http://localhost/matomo/',  // Matomo local sur Windows (optionnel)
        siteId: 1,                       // ID du site dans Matomo local
        enableLinkTracking: true         // Active le tracking des liens
      };
    }
    
    // CONFIGURATION PRODUCTION - utilisation du sous-domaine HTTPS dédié
    return {
      environment: 'production', 
      url: 'https://stats.offisport.com/',  // Sous-domaine dédié Matomo
      siteId: 1,                          // ID du site dans Matomo production
      enableLinkTracking: true,
      adminUrl: 'https://stats.offisport.com/'  // URL complète pour l'administration
    };
  }

  /**
   * Initialisation de Matomo
   * 
   * Cette méthode :
   * 1. Vérifie si Matomo n'est pas déjà initialisé
   * 2. Crée l'objet global _paq (queue des commandes Matomo)
   * 3. Configure les paramètres de base
   * 4. Charge le script Matomo de façon asynchrone
   */
  init() {
    // Éviter la double initialisation
    if (!this.config.enableLinkTracking || this.isInitialized) return;

    try {
      // CRÉATION DE LA QUEUE MATOMO
      // _paq est un tableau global qui stocke les commandes avant le chargement complet
      window._paq = window._paq || [];
      
      // CONFIGURATION DE BASE
      window._paq.push(['trackPageView']);      // Active le tracking des pages
      window._paq.push(['enableLinkTracking']); // Active le tracking des clics sur liens
      
      // CONFIGURATION DU SITE
      window._paq.push(['setTrackerUrl', this.config.url + 'matomo.php']); // URL du tracker
      window._paq.push(['setSiteId', this.config.siteId]);                 // ID du site

      // CHARGEMENT ASYNCHRONE DU SCRIPT
      this.loadScript();
      
      this.isInitialized = true;
      console.log(`✅ Matomo initialisé (${this.config.environment})`);
      
    } catch (error) {
      console.warn('⚠️ Erreur initialisation Matomo:', error);
    }
  }

  /**
   * Chargement asynchrone du script Matomo
   * 
   * Charge le script JavaScript de Matomo sans bloquer le rendu de la page.
   * Gère les erreurs gracieusement (notamment en développement).
   */
  loadScript() {
    // Création de l'élément script
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;    // Chargement asynchrone (non bloquant)
    script.defer = true;    // Exécution différée
    script.src = this.config.url + 'matomo.js';  // URL du script Matomo
    
    // GESTION DES ERREURS DE CHARGEMENT
    script.onerror = () => {
      if (this.config.environment === 'development') {
        console.warn('⚠️ Matomo local non disponible (normal en développement)');
      } else {
        console.warn('⚠️ Erreur chargement script Matomo');
      }
    };
    
    // INSERTION DU SCRIPT DANS LE DOM
    // L'insère avant le premier script existant pour un chargement prioritaire
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(script, firstScript);
  }

  /**
   * Mapping des routes vers des titres explicites
   * 
   * Convertit les URLs techniques (/actualites) en titres lisibles
   * pour les rapports Matomo.
   * 
   * @param {string} pathname - Chemin de l'URL actuelle
   * @returns {string} Titre lisible pour Matomo
   */
  getPageTitle(pathname) {
    const routeTitles = {
      '/': 'Accueil - OFFISPORT',
      '/accueil': 'Accueil - OFFISPORT',
      '/actualites': 'Actualités - OFFISPORT',
      '/activites': 'Activités - OFFISPORT',
      '/evenements': 'Événements - OFFISPORT',
      '/clubs': 'Clubs - OFFISPORT',
      '/bibliotheque': 'Bibliothèque - OFFISPORT',
      '/partenaires': 'Partenaires - OFFISPORT',
      '/contact': 'Contact - OFFISPORT'
    };
    
    // Retourne le titre mappé ou un titre générique
    return routeTitles[pathname] || `${pathname} - OFFISPORT`;
  }

  /**
   * Tracking d'une page vue
   * 
   * IMPORTANT POUR LES SPA (Single Page Applications) :
   * React Router ne recharge pas la page, donc il faut tracker
   * manuellement chaque changement de "page".
   * 
   * @param {string} customTitle - Titre personnalisé (optionnel)
   */
  trackPageView(customTitle = null) {
    if (!this.isInitialized) return;

    try {
      window._paq = window._paq || [];
      
      // Générer un titre de page explicite si aucun titre personnalisé
      const pageTitle = customTitle || this.getPageTitle(window.location.pathname);
      
      // CONFIGURATION DE LA PAGE VUE
      window._paq.push(['setDocumentTitle', pageTitle]);           // Titre de la page
      window._paq.push(['setCustomUrl', window.location.href]);    // URL complète
      window._paq.push(['trackPageView']);                         // Enregistrer la vue
      
      console.log(`📊 Page vue trackée: ${pageTitle}`);
    } catch (error) {
      console.warn('⚠️ Erreur tracking page vue:', error);
    }
  }

  /**
   * Tracking d'un événement personnalisé
   * 
   * EXEMPLES D'USAGE :
   * - Clic sur un bouton : trackEvent('Interface', 'Clic', 'Bouton Contact')
   * - Téléchargement : trackEvent('Téléchargement', 'PDF', 'Guide Activités')
   * - Erreur : trackEvent('Erreur', 'API', 'Échec chargement actualités')
   * 
   * @param {string} category - Catégorie de l'événement
   * @param {string} action - Action effectuée
   * @param {string} name - Nom spécifique (optionnel)
   * @param {number} value - Valeur numérique (optionnel)
   */
  trackEvent(category, action, name = null, value = null) {
    if (!this.isInitialized) return;

    try {
      window._paq = window._paq || [];
      window._paq.push(['trackEvent', category, action, name, value]);
      console.log(`📊 Événement tracké: ${category} > ${action} ${name ? '> ' + name : ''}`);
    } catch (error) {
      console.warn('⚠️ Erreur tracking événement:', error);
    }
  }

  /**
   * Tracking du temps passé sur une page/action
   * 
   * UTILISATION :
   * - Temps de lecture d'un article
   * - Temps passé sur une page
   * - Durée d'une action
   * 
   * @param {string} category - Catégorie (ex: "Performance")
   * @param {string} action - Action (ex: "Temps lecture")
   * @param {number} timeMs - Temps en millisecondes
   */
  trackTiming(category, action, timeMs) {
    if (!this.isInitialized) return;

    try {
      window._paq = window._paq || [];
      // Utilise trackEvent avec une valeur en millisecondes
      window._paq.push(['trackEvent', category, action, 'temps_ms', timeMs]);
      console.log(`⏱️ Timing tracké: ${action} = ${timeMs}ms`);
    } catch (error) {
      console.warn('⚠️ Erreur tracking timing:', error);
    }
  }

  /**
   * Tracking d'un objectif/conversion
   * 
   * OBJECTIFS POSSIBLES :
   * - Inscription à la newsletter
   * - Téléchargement d'un document
   * - Demande de contact
   * - Temps passé important sur le site
   * 
   * @param {number} goalId - ID de l'objectif configuré dans Matomo
   * @param {number} customRevenue - Valeur monétaire (optionnel)
   */
  trackGoal(goalId, customRevenue = null) {
    if (!this.isInitialized) return;

    try {
      window._paq = window._paq || [];
      window._paq.push(['trackGoal', goalId, customRevenue]);
      console.log(`🎯 Objectif tracké: Goal ${goalId}`);
    } catch (error) {
      console.warn('⚠️ Erreur tracking objectif:', error);
    }
  }

  /**
   * Définir des variables personnalisées
   * 
   * VARIABLES UTILES :
   * - Type d'utilisateur (membre/visiteur)
   * - Section préférée
   * - Paramètres d'affichage
   * 
   * @param {number} index - Index de la variable (1-5)
   * @param {string} name - Nom de la variable
   * @param {string} value - Valeur de la variable
   * @param {string} scope - Portée ('page' ou 'visit')
   */
  setCustomVariable(index, name, value, scope = 'page') {
    if (!this.isInitialized) return;

    try {
      window._paq = window._paq || [];
      window._paq.push(['setCustomVariable', index, name, value, scope]);
      console.log(`🔧 Variable personnalisée: ${name} = ${value}`);
    } catch (error) {
      console.warn('⚠️ Erreur variable personnalisée:', error);
    }
  }

  /**
   * Tracking des erreurs JavaScript
   * 
   * USAGE AUTOMATIQUE :
   * Ce service est appelé automatiquement par useErrorTracking()
   * pour envoyer toutes les erreurs JS non gérées à Matomo.
   * 
   * @param {Error} error - Objet erreur JavaScript
   * @param {Object} info - Informations contextuelles (optionnel)
   */
  trackError(error, info = null) {
    if (!this.isInitialized) return;

    try {
      const errorMessage = error.message || error.toString();
      const errorStack = error.stack || 'Pas de stack trace';
      
      // Utilise trackEvent pour enregistrer l'erreur
      this.trackEvent('JavaScript Error', errorMessage, errorStack);
      console.error(`🐛 Erreur trackée: ${errorMessage}`);
    } catch (trackingError) {
      console.warn('⚠️ Erreur tracking erreur:', trackingError);
    }
  }

  /**
   * Obtenir l'URL Matomo pour l'administration
   * 
   * @returns {string} URL d'accès à l'interface Matomo
   */
  getAdminUrl() {
    return this.config.url;
  }

  /**
   * Vérifier si Matomo est actif
   * 
   * @returns {boolean} true si Matomo est initialisé et actif
   */
  isActive() {
    return this.isInitialized && this.config.enableLinkTracking;
  }
}

// PATTERN SINGLETON
// Créer une instance unique du service qui sera partagée dans toute l'application
const matomoService = new MatomoService();

// Export par défaut de l'instance (pas de la classe)
export default matomoService; 